import type Luminary from '@lumen5/luminary';
import type { AppDispatch, GetRootState } from 'js/src/libs/RootState';

import { type AutoTimingResult, autoTimeAssets } from './asset-auto-timing';

import { TOOL_NAMES } from 'js/src/agent/toolNames';

/**
 * Lint's auto-fixes: unlike the linter's checks, which only read the video, these change it. They
 * run before the checks (so the checks see the fixed video), only for the root agent (a subagent
 * may be a read-only reviewer), and each can also be run alone by passing its name as check_name.
 */
export interface AutoFixContext {
    luminary: Luminary;
    dispatch: AppDispatch;
    getState: GetRootState;
    parentActionGroup: string;
    formatSceneLocator: (sceneId: string) => string;
}

export interface AutoFixResult {
    fixedCount: number;
    /** What changed, for the top of the lint report. Empty when nothing did. */
    note: string;
}

interface AutoFix {
    name: string;
    run: (context: AutoFixContext) => Promise<AutoFixResult>;
}

/**
 * Tells the agent which assets were just timed for it, so it doesn't re-time them by hand and can
 * still override one that landed somewhere it didn't intend, and which it left in place because
 * their word needs the scene re-timed first. Empty when neither happened.
 */
export function formatAutoTimingNote(
    { timed, held }: AutoTimingResult,
    formatSceneLocator: (sceneId: string) => string,
): string {
    const sections: string[] = [];
    if (timed.length > 0) {
        sections.push(
            `Timed ${timed.length} text/icon asset(s) onto the narration word each one introduces:`,
            ...timed.map(
                ({ sceneId, assetName, startWords }) =>
                    `  - [${formatSceneLocator(sceneId)}] "${assetName}" now appears on "${startWords.join(' ')}"`,
            ),
            `Leave these as they are unless one is clearly wrong for this video; to move one, use ${TOOL_NAMES.setText} or ${TOOL_NAMES.setIcon} with start_words.`,
        );
    }
    if (held.length > 0) {
        sections.push(
            `Left ${held.length} text/icon asset(s) where they are rather than on the word that introduces them:`,
            ...held.map(
                ({ sceneId, assetName, reason }) =>
                    `  - [${formatSceneLocator(sceneId)}] "${assetName}" left where it is: ${reason}`,
            ),
        );
    }
    return sections.length > 0 ? [...sections, '', ''].join('\n') : '';
}

/** Lands every text and icon nobody has timed on the narration word it introduces. */
const assetTiming = {
    name: 'asset_timing',
    run: async ({ formatSceneLocator, ...context }) => {
        const result = await autoTimeAssets(context);
        return {
            fixedCount: result.timed.length,
            note: formatAutoTimingNote(result, formatSceneLocator),
        };
    },
} as const satisfies AutoFix;

export const AUTO_FIXES = [assetTiming] as const;

export type AutoFixName = (typeof AUTO_FIXES)[number]['name'];

export const ALL_AUTO_FIXES = AUTO_FIXES.map(({ name }) => name) as AutoFixName[];

export const isAutoFixName = (name: string | undefined): name is AutoFixName =>
    ALL_AUTO_FIXES.includes(name as AutoFixName);
